import { Boundaries } from "@planzone/core";
import { events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = {
  request: {
    isPending: false,
    isSuccessful: false,
    isFailure: false,
  },
  statuses: [],
};

function handleRequest(state: State): State {
  return { ...state, request: setRequestStateToPending() };
}

function handleSuccess(
  state: State,
  statuses: Boundaries["retrieveProjectStatuses"]["success"],
): State {
  return { statuses, request: setRequestStateToSuccess() };
}

function handleFailure(state: State): State {
  return { ...state, request: setRequestStateToFailure() };
}

function handleProjectStatusCreationRequest(
  state: State,
  { status }: Boundaries["createProjectStatus"]["request"],
): State {
  return {
    ...state,
    statuses: [...state.statuses, { ...status, uuid: "" }],
  };
}

function handleProjectStatusCreationSuccess(
  state: State,
  { status }: Boundaries["createProjectStatus"]["success"],
): State {
  return {
    ...state,
    statuses: state.statuses.map((s) =>
      status.label === s.label ? status : s,
    ),
  };
}

function handleProjectStatusCreationFailure(
  state: State,
  { status }: Boundaries["createProjectStatus"]["failure"],
): State {
  return {
    ...state,
    statuses: state.statuses.filter(({ label }) => label !== status.label),
  };
}

function handleRemoveProjectStatusRequest(
  state: State,
  status: Boundaries["removeProjectStatus"]["request"],
): State {
  return {
    ...state,
    statuses: state.statuses.filter(({ uuid }) => uuid !== status.uuid),
  };
}

function handleRemoveProjectStatusFailure(
  state: State,
  status: Boundaries["removeProjectStatus"]["failure"],
): State {
  return {
    ...state,
    statuses: [...state.statuses, status],
  };
}

function handleEditProjectStatusRequest(
  state: State,
  { status, nextStatus }: Boundaries["editProjectStatus"]["request"],
): State {
  return {
    ...state,
    statuses: state.statuses.map((s) =>
      s.uuid === status.uuid ? { ...s, ...nextStatus } : s,
    ),
  };
}

function handleEditProjectStatusSuccess(
  state: State,
  { status }: Boundaries["editProjectStatus"]["success"],
): State {
  return {
    ...state,
    statuses: state.statuses.map((s) => (s.uuid === status.uuid ? status : s)),
  };
}

function handleEditProjectStatusFailure(
  state: State,
  { status }: Boundaries["editProjectStatus"]["failure"],
): State {
  return {
    ...state,
    statuses: state.statuses.map((s) =>
      s.uuid === status.uuid ? { ...s, ...status } : s,
    ),
  };
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.retrieveProjectStatusesRequested]: handleRequest,
    [events.retrieveProjectStatusesSucceeded]: handleSuccess,
    [events.retrieveProjectStatusesFailed]: handleFailure,
    [events.createProjectStatusRequested]: handleProjectStatusCreationRequest,
    [events.createProjectStatusSucceeded]: handleProjectStatusCreationSuccess,
    [events.createProjectStatusFailed]: handleProjectStatusCreationFailure,
    [events.removeProjectStatusRequested]: handleRemoveProjectStatusRequest,
    [events.removeProjectStatusFailed]: handleRemoveProjectStatusFailure,
    [events.editProjectStatusRequested]: handleEditProjectStatusRequest,
    [events.editProjectStatusSucceeded]: handleEditProjectStatusSuccess,
    [events.editProjectStatusFailed]: handleEditProjectStatusFailure,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
